<template>
  <div class="modal-overlay top fight-backdrop" @click.self="$emit('close')">
    <div class="fight-modal" role="dialog" aria-modal="true" aria-label="Fight view">
      <button class="close-btn" @click="$emit('close')" aria-label="Close">×</button>

      <div class="roster-select">
        <div class="roster-side left-side" :style="teamBoxStyle(leftTeam.label)">
          <span class="roster-wm" :style="{ color: teamColor(leftTeam.label) }">{{ leftTeam.label }}</span>
          <div class="roster-units">
            <button v-for="(u, i) in leftTeam.roster" :key="i"
                    :class="['roster-unit', { active: attackerSide === 'left' && attackerIndex === i }]"
                    @click="pick('left', i)">
              <img class="ru-icon" :src="portrait(u, leftTeam.label)"
                   :alt="u.Class" @error="$event.target.style.visibility='hidden'">
              <span class="ru-name">{{ u.Name }}</span>
            </button>
          </div>
        </div>
        <div class="roster-vs">vs</div>
        <div class="roster-side" :style="teamBoxStyle(rightTeam.label)">
          <span class="roster-wm" :style="{ color: teamColor(rightTeam.label) }">{{ rightTeam.label }}</span>
          <div class="roster-units">
            <button v-for="(u, i) in rightTeam.roster" :key="i"
                    :class="['roster-unit', { active: attackerSide === 'right' && attackerIndex === i }]"
                    @click="pick('right', i)">
              <img class="ru-icon" :src="portrait(u, rightTeam.label)"
                   :alt="u.Class" @error="$event.target.style.visibility='hidden'">
              <span class="ru-name">{{ u.Name }}</span>
            </button>
          </div>
        </div>
      </div>
      <p class="pick-hint">Click any unit to see its attacks vs the other team.</p>

      <div v-if="terrain" class="fight-map">
        <div class="fight-map-label">🗺 {{ map }} — starting positions</div>
        <MapViewer3D :key="'fm-' + map" :terrain="terrain" :starts="mapStarts" :mesh-url="meshUrl" />
      </div>

      <div v-if="predict" class="fm-predict" :class="predict.winner ? 'graded' : 'open'">
        <template v-if="!predict.winner">
          <span class="fmp-q">🔮 Predict the winner:</span>
          <button class="fmp-btn" :class="{ picked: predict.picked === leftTeam.name }"
                  :style="{ '--tc': teamColor(leftTeam.label) }" @click="$emit('pick-winner', leftTeam.name)">
            <span v-if="predict.picked === leftTeam.name">🔮 </span>{{ leftTeam.label }}
          </button>
          <button class="fmp-btn" :class="{ picked: predict.picked === rightTeam.name }"
                  :style="{ '--tc': teamColor(rightTeam.label) }" @click="$emit('pick-winner', rightTeam.name)">
            <span v-if="predict.picked === rightTeam.name">🔮 </span>{{ rightTeam.label }}
          </button>
          <span class="fmp-meta">{{ predict.roundLabel }} · {{ predict.weight }} pt{{ predict.weight > 1 ? 's' : '' }}</span>
        </template>
        <template v-else>
          <span class="fmp-res" :class="!predict.picked ? 'none' : (predict.picked === predict.winner ? 'hit' : 'miss')">
            <template v-if="!predict.picked">— you didn't pick this match</template>
            <template v-else-if="predict.picked === predict.winner">✓ you nailed it (+{{ predict.weight }})</template>
            <template v-else>✗ you picked {{ teamLabelFor(predict.picked) }}</template>
          </span>
          <span v-if="predict.simPick" class="fmp-sim">🤖 sim: {{ teamLabelFor(predict.simPick) }} {{ predict.simPick === predict.winner ? '✓' : '✗' }}</span>
        </template>
      </div>

      <div class="sim-bar">
        <button class="sim-btn" @click="simulate">⚔ Simulate ({{ simRuns }}×)</button>
        <template v-if="simResult">
          <span class="sim-pct" :style="{ color: teamColor(leftTeam.label) }">{{ leftTeam.label }} {{ simOdds.a }}%</span>
          <span class="sim-vs">–</span>
          <span class="sim-pct" :style="{ color: teamColor(rightTeam.label) }">{{ rightTeam.label }} {{ simOdds.b }}%</span>
          <span class="sim-ci" title="95% confidence margin from the number of runs">±{{ winMargin() }}%</span>
          <span v-if="simResult.draws" class="sim-draw">({{ Math.round(simResult.draws / simResult.n * 100) }}% draw)</span>
          <button class="sim-log-toggle" @click="showSimLog = !showSimLog">{{ showSimLog ? 'hide log' : 'log' }}</button>
          <span class="sim-note">estimate — abstract grid sim (own statuses; matrix toggles don't apply)</span>
        </template>
      </div>
      <div v-if="simResult && simResult.unitStats && simResult.unitStats.length" class="sim-survival">
        <div class="ss-col">
          <div class="ss-head" :style="{ color: teamColor(leftTeam.label) }">{{ leftTeam.label }} · survives to end</div>
          <div v-for="u in survivalRows().A" :key="'sa' + u.name" class="ss-row"><span class="ss-name">{{ u.name }}</span><span class="ss-pct">{{ Math.round(u.survivalPct * 100) }}%</span></div>
        </div>
        <div class="ss-col">
          <div class="ss-head" :style="{ color: teamColor(rightTeam.label) }">{{ rightTeam.label }} · survives to end</div>
          <div v-for="u in survivalRows().B" :key="'sb' + u.name" class="ss-row"><span class="ss-name">{{ u.name }}</span><span class="ss-pct">{{ Math.round(u.survivalPct * 100) }}%</span></div>
        </div>
      </div>
      <div v-if="simFrames && simFrames.length" class="sim-replay">
        <div class="sim-map-row">
          <div v-if="map" class="sim-map">🗺 {{ map }}<span v-if="!terrain" class="sim-map-flat"> · flat grid (no terrain data)</span></div>
          <button v-if="simRepro" class="sim-repro-btn" @click="copyRepro"
                  title="Copy a reproducible capsule of THIS exact fight (seed + both rosters + map). Paste it into replayRepro() to replay/debug the identical battle.">{{ reproCopied ? '✓ copied' : '📋 copy repro' }}</button>
        </div>
        <BattleViewer :frames="simFrames" :terrain="terrain" :team-a="leftTeam.label" :team-b="rightTeam.label" />
      </div>
      <div v-if="showSimLog && simLog.length" class="sim-log">
        <div class="sim-log-head">{{ sampleLabel() }}</div>
        <div v-for="(l, i) in simLog" :key="i">{{ l }}</div>
      </div>

      <div class="turn-order">
        <span class="to-label" title="Estimated from Speed/CT assuming an equal start. Accounts for gear-granted Haste/Slow; not in-battle Haste/Slow.">Est. turn order →</span>
        <div class="to-strip">
          <div v-for="(t, i) in turnOrderList" :key="i" :class="['to-unit', 'to-' + t.side]">
            <span class="to-num">{{ i + 1 }}</span>
            <img class="to-icon" :class="{ flip: t.side === 'left' }" :src="portrait(t.unit, t.teamLabel)"
                 :alt="t.name" @error="$event.target.style.visibility='hidden'">
            <span class="to-name">{{ t.name }}</span>
            <span v-if="t.haste" class="to-status haste" title="Haste (×3/2 Speed)">⏫</span>
            <span v-else-if="t.slow" class="to-status slow" title="Slow (×1/2 Speed)">⏬</span>
          </div>
        </div>
      </div>

      <div class="fight-body">
        <div class="attacker-panel">
          <div class="ap-identity">
            <img class="ap-portrait" :src="portrait(attackerUnit, attackerTeam.label)" :alt="attackerUnit.Class"
                 @error="$event.target.style.visibility='hidden'">
            <div>
              <div class="ap-name">{{ attackerUnit.Name }}</div>
              <div class="ap-class">{{ attackerUnit.Class }} · {{ attackerUnit.Sign }}</div>
              <div class="ap-bf">Brave {{ attackerUnit.Brave }} · Faith {{ attackerUnit.Faith }}</div>
            </div>
          </div>
          <div class="ap-block ap-stats">
            <span>HP {{ attacker.stats.hp }}</span><span>MP {{ attacker.stats.mp }}</span>
            <span>PA {{ attacker.stats.pa }}</span><span>MA {{ attacker.stats.ma }}</span>
            <span>SP {{ attacker.stats.speed }}</span>
            <span>MV {{ attacker.stats.move }}</span><span>JP {{ attacker.stats.jump }}</span>
          </div>
          <div class="ap-block ap-skills">
            <div v-if="attackerUnit.ActionSkill">Action: {{ attackerUnit.ActionSkill }}</div>
            <div v-if="attackerUnit.ReactionSkill">React: {{ attackerUnit.ReactionSkill }}</div>
            <div v-if="attackerUnit.SupportSkill">Support: {{ attackerUnit.SupportSkill }}</div>
            <div v-if="attackerUnit.MoveSkill">Move: {{ attackerUnit.MoveSkill }}</div>
          </div>
          <div class="ap-block ap-gear">
            <div v-for="g in gearList" :key="g.slot">{{ g.slot }}: {{ g.name }}</div>
          </div>
          <div v-if="affinityText" class="ap-block ap-affinity">{{ affinityText }}</div>
        </div>

        <div class="matrix-wrap">
          <p v-if="isMime" class="mime-note">
            Mime — copies teammates' actions, so this lists the team's abilities
            (computed with {{ attackerUnit.Name }}'s stats). Most skills are
            mimeable.
          </p>
          <p v-if="!abilityRows.length" class="no-abilities">
            {{ attackerUnit.Name }} has no recognized damaging abilities.
          </p>
          <table v-else class="fight-matrix">
            <thead>
              <tr>
                <th class="ability-col">Ability</th>
                <th v-for="(d, c) in defenders" :key="c" class="def-col">
                  <div class="def-name">{{ d.unit.Name }}</div>
                  <div class="def-class">{{ d.unit.Class }}</div>
                  <div class="def-zodiac" :class="'z-' + d.zodiac.toLowerCase()">{{ d.zodiac }}</div>
                  <div v-if="d.affinity" class="def-affinity">{{ d.affinity }}</div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, r) in grid" :key="r">
                <td class="ability-col">
                  {{ row.ability }}
                  <span v-if="row.element" class="el-tag">{{ row.element }}</span>
                  <span v-if="row.status" class="el-tag status-tag">status %</span>
                </td>
                <td v-for="(cell, c) in row.cells" :key="c"
                    :class="['cell', 'fx-' + (cell.effect || 'none'), { open: isOpen(r, c) }]"
                    @click="toggle(r, c)">
                  <span class="cell-dmg">{{ cell.display }}</span>
                  <span v-if="cell.hit != null" class="cell-hit">{{ cell.hit }}% hit</span>
                </td>
              </tr>
            </tbody>
          </table>

          <div v-if="openCell" class="drilldown">
            <div class="drill-head">
              {{ grid[expanded.r].ability }} → {{ defenders[expanded.c].unit.Name }}
            </div>
            <div class="drill-steps">
              <span v-for="(s, i) in openCell.steps" :key="i" class="drill-step">
                <span class="step-label">{{ s.label }}</span>
                <span v-if="s.factor" class="step-factor">{{ s.factor }}</span>
                <span class="step-value">{{ s.value }}</span>
              </span>
              <span v-if="!openCell.steps.length" class="drill-step">No modifiers — base damage.</span>
            </div>
            <div v-if="openCell.kind === 'damage'" class="drill-hit">
              <template v-if="openCell.hitDirs && openCell.hitDirs.magical">
                Magic hit chance (any direction): {{ openCell.hitDirs.front }}%
              </template>
              <template v-else-if="openCell.hitDirs">
                Hit chance — Front {{ openCell.hitDirs.front }}% · Side {{ openCell.hitDirs.side }}% · Back {{ openCell.hitDirs.back }}%
              </template>
              <template v-else>Can't be evaded.</template>
            </div>
          </div>

          <div class="status-row">
            <span class="status-label">Pretend each target has:</span>
            <label><input type="checkbox" v-model="targetStatuses.protect"> Protect</label>
            <label><input type="checkbox" v-model="targetStatuses.shell"> Shell</label>
            <label><input type="checkbox" v-model="targetStatuses.charging"> Charging</label>
            <label><input type="checkbox" v-model="targetStatuses.sleeping"> Sleeping</label>
            <label><input type="checkbox" v-model="targetStatuses.frogChicken"> Frog/Chicken</label>
            <label><input type="checkbox" v-model="targetStatuses.oil"> Oil (weak Fire)</label>
          </div>
          <p class="matrix-note">
            Numbers are estimates: attacker Strengthen/Attack-Up, the target's
            gear Absorb/Half, zodiac, and faith are applied. Green = absorbed
            (heals target), red = weak ×2, blue = halved. Click a cell for the
            step breakdown.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { unitCombatStats } from '../lib/unitStats.js';
import { unitElementProfile } from '../lib/elements.js';
import { zodiacCompat } from '../lib/zodiac.js';
import { computeAbilityDamage, computeSuccess, parseAbilityInfo, legacyAbilityFormula } from '../lib/abilityFormula.js';
import { weaponAttackModel, unarmedAttackModel, jumpAttackModel } from '../lib/damage.js';
import { hitChance } from '../lib/evasion.js';
import { turnOrder } from '../lib/turnOrder.js';

import { teamBright } from '../lib/teamColors.js';
import { prepTeam } from '../lib/simPrep.js';
import { runBattles, simulateBattle, serializeRepro } from '../lib/battleSim.js';
import { calibratedOdds } from '../lib/simCalibrationLive.js';
import modalA11y from '../mixins/modalA11y.js';
import battleConfig from '../data/battleConfig.json';
import mapTerrain from '../data/mapTerrain.json';
import mapTiles from '../data/mapTiles.json';
import { tilesForMap } from '../lib/fftPath.js';
import monsterSkills from '../data/monsterSkills.json';
import { MESH_URLS } from '../lib/meshUrls.js';
import { startsWithSprites } from '../lib/spriteForClass.js';
import BattleViewer from './BattleViewer.vue';
import { defineAsyncComponent } from 'vue';
// Lazy — three.js (~600KB) only loads when the Fight View actually opens.
const MapViewer3D = defineAsyncComponent(() => import('./MapViewer3D.vue'));

import { UNITS_BASE as PORTRAIT_BASE } from '../lib/assetUrls.js';

export default {
  mixins: [modalA11y],
  props: {
    map: { type: String, default: '' },           // this round's map title, e.g. "12) Bariaus Hill"
    leftTeam: { type: Object, required: true },   // { name, label, roster }
    rightTeam: { type: Object, required: true },
    itemschanged: { type: Object, required: true },
    charStats: { type: Object, required: true },
    abilitiesInfo: { type: Object, default: () => ({}) },
    notAbles: { type: Object, required: true },
    toolTips: { type: Object, required: true },
    // Predict-the-Bracket state for this matchup, or null. Lets the player pick the
    // winner from the Fight View. { candidates, picked, simPick, winner, weight, roundLabel }
    predict: { type: Object, default: null },
  },
  emits: ['close', 'pick-winner'],
  components: { BattleViewer, MapViewer3D },

  data() {
    return {
      attackerSide: 'left',
      attackerIndex: 0,
      expanded: null, // { r, c }
      targetStatuses: {
        protect: false, shell: false, charging: false,
        sleeping: false, frogChicken: false, oil: false,
      },
      // Battle-sim (win %) state.
      simResult: null, simLog: [], simSample: null, simFrames: null, showSimLog: false, simRuns: battleConfig.runs || 150,
      simRepro: null, reproCopied: false, // reproducible-fight capsule for the sampled battle
    };
  },

  computed: {
    // The real map's terrain (height/walls/void + ENTD starts) for this round, from
    // the "NN) Name" map title — prefer a per-name override, else the number key.
    // null ⇒ the sim falls back to a flat grid.
    terrain() {
      const m = (this.map || '').match(/^\s*(\d+)\)\s*(.*)$/);
      const num = m ? +m[1] : null;
      const bare = (m ? m[2] : this.map || '').trim();
      return (mapTerrain.byName && mapTerrain.byName[bare]) || (num != null && mapTerrain.maps ? mapTerrain.maps[num] : null) || null;
    },
    // The game's own tile table for this map, so the sim walks by FFT's pathfinder (null ⇒ the old BFS).
    tiles() { return tilesForMap(this.map, mapTiles); },
    // Real .glb mesh for the map view, when this isn't a displaced (byName) map.
    meshUrl() {
      const m = (this.map || '').match(/^\s*(\d+)\)\s*(.*)$/);
      if (!m) return null;
      if (mapTerrain.byName && mapTerrain.byName[m[2].trim()]) return null;   // displaced → schematic only
      return MESH_URLS[+m[1]] || null;
    },
    // The map's start tiles tagged with each team's real units (in team colors),
    // for the map view — same placement the bracket-hover popup uses.
    mapStarts() {
      return startsWithSprites(this.terrain && this.terrain.starts, this.leftTeam, this.rightTeam);
    },
    // Head-to-head odds: the sim's raw win rate (simCalibrationLive shows it unadjusted since
    // 2026-10-16; champSide only matters if a calibrated display is switched back on).
    simOdds() {
      if (!this.simResult) return null;
      const champSide = this.leftTeam.label === 'Champion' ? 'A' : this.rightTeam.label === 'Champion' ? 'B' : null;
      return calibratedOdds(this.simResult, { champSide });
    },
    attackerTeam() { return this.attackerSide === 'left' ? this.leftTeam : this.rightTeam; },
    defenderTeam() { return this.attackerSide === 'left' ? this.rightTeam : this.leftTeam; },
    attackerUnit() { return this.attackerTeam.roster[this.attackerIndex]; },

    attacker() {
      const s = unitCombatStats(this.attackerUnit, this.charStats, this.itemschanged, this.notAbles);
      return { ...s, profile: unitElementProfile(s.equipped) };
    },

    gearList() {
      const u = this.attackerUnit;
      return [['Hand', u.Mainhand], ['Off', u.Offhand], ['Head', u.Head], ['Body', u.Armor], ['Acc', u.Accessory]]
        .filter(([, name]) => name)
        .map(([slot, name]) => ({ slot, name }));
    },
    affinityText() {
      const p = this.attacker.profile;
      const bits = [];
      if (p.strengthen.size) bits.push('Strengthen ' + [...p.strengthen].join('/'));
      if (p.absorb.size) bits.push('Absorb ' + [...p.absorb].join('/'));
      if (p.half.size) bits.push('Half ' + [...p.half].join('/'));
      if (p.weak.size) bits.push('Weak ' + [...p.weak].join('/'));
      return bits.join('  ·  ');
    },
    attackerMods() {
      return {
        strengthen: this.attacker.profile.strengthen,
        attackUp: this.attackerUnit.SupportSkill === 'Attack Up',
        magicAttackUp: this.attackerUnit.SupportSkill === 'Magic Attack Up',
        // Martial Arts (support, or Monk innate) → ×3/2 on Punch Art abilities.
        martialArts: this.attackerUnit.SupportSkill === 'Martial Arts' || this.attackerUnit.Class === 'Monk',
      };
    },

    // Resolved defenders with stats, elemental profile, zodiac vs attacker.
    defenders() {
      const a = this.attackerUnit;
      return this.defenderTeam.roster.map((u) => {
        const s = unitCombatStats(u, this.charStats, this.itemschanged, this.notAbles);
        const profile = unitElementProfile(s.equipped);
        const affinityBits = [];
        if (profile.absorb.size) affinityBits.push('Abs ' + [...profile.absorb].join('/'));
        if (profile.half.size) affinityBits.push('½ ' + [...profile.half].join('/'));
        return {
          unit: u, stats: s.stats, profile,
          equipped: s.equipped, cEv: s.classBase.cEvPercent, reaction: u.ReactionSkill,
          zodiac: zodiacCompat(a.Gender, a.Sign, u.Gender, u.Sign),
          affinity: affinityBits.join('  '),
          mods: {
            defenseUp: u.SupportSkill === 'Defense Up',
            magicDefendUp: u.SupportSkill === 'Magic Defense Up',
            protect: this.targetStatuses.protect,
            shell: this.targetStatuses.shell,
            charging: this.targetStatuses.charging,
            sleeping: this.targetStatuses.sleeping,
            frogChicken: this.targetStatuses.frogChicken,
            zodiac: zodiacCompat(a.Gender, a.Sign, u.Gender, u.Sign),
            absorb: profile.absorb,
            half: profile.half,
            weak: new Set([...profile.weak, ...(this.targetStatuses.oil ? ['Fire'] : [])]),
          },
        };
      });
    },

    isMime() { return this.attackerUnit.Class === 'Mime'; },

    // Estimated turn order across both teams (CT/Speed sim). Tie-break order
    // is L1, R1, L2, R2, … so we interleave the rosters.
    turnUnits() {
      const mk = (u, team, side) => {
        const cs = unitCombatStats(u, this.charStats, this.itemschanged, this.notAbles);
        // Initial/permanent Haste or Slow from gear adjusts effective Speed
        // (Haste ×3/2, Slow ×1/2). In-battle Haste/Slow cast mid-fight isn't
        // modeled (we can't know it).
        const statuses = [];
        for (const item of Object.values(cs.equipped)) {
          for (const s of (item.permstatus || [])) statuses.push(s.name);
          for (const s of (item.initstatus || [])) statuses.push(s.name);
        }
        const haste = statuses.includes('Haste');
        const slow = !haste && statuses.includes('Slow');
        let speed = cs.stats.speed;
        if (haste) speed = Math.floor((speed * 3) / 2);
        else if (slow) speed = Math.floor(speed / 2);
        return { name: u.Name, teamLabel: team.label, side, unit: u, speed, haste, slow };
      };
      const out = [];
      const L = this.leftTeam.roster, R = this.rightTeam.roster;
      for (let i = 0; i < Math.max(L.length, R.length); i++) {
        if (L[i]) out.push(mk(L[i], this.leftTeam, 'left'));
        if (R[i]) out.push(mk(R[i], this.rightTeam, 'right'));
      }
      return out;
    },
    turnOrderList() { return turnOrder(this.turnUnits, 12); },

    // Rows: basic Attack(s), Jump (if able), then damaging + status abilities.
    // A Mime mimics teammates, so its ability rows are sourced from the team.
    abilityRows() {
      const rows = [];
      const u = this.attackerUnit;
      const eq = this.attacker.equipped;
      const dual = u.SupportSkill === 'Dual Wield';

      // Basic attack(s). Dual Wield -> one row per weapon; otherwise mainhand
      // (Doublehand's ×2 is baked into weaponAttackModel), else unarmed.
      const wMain = eq.Mainhand && eq.Mainhand.wp ? weaponAttackModel(eq.Mainhand, u) : null;
      const wOff = dual && eq.Offhand && eq.Offhand.wp ? weaponAttackModel(eq.Offhand, u) : null;
      if (wMain) rows.push({ label: `Attack: ${u.Mainhand}`, model: wMain, element: wMain.element, evadable: eq.Mainhand.type !== 'Gun' });
      if (wOff) rows.push({ label: `Attack: ${u.Offhand}`, model: wOff, element: wOff.element, evadable: eq.Offhand.type !== 'Gun' });
      if (!wMain && !wOff) rows.push({ label: 'Attack: Unarmed', model: unarmedAttackModel(u), element: null, evadable: true });

      // Jump — present when the unit has a Jump-range skill.
      const skills = [...(u.ClassSkills || []), ...(u.ExtraSkills || [])];
      if (skills.some((s) => /^(Level|Vertical) Jump/.test(s || ''))) {
        rows.push({ label: 'Jump', model: jumpAttackModel(eq.Mainhand, u), element: null, attackerMods: {}, evadable: false });
      }

      // Damaging + status abilities.
      const sourceUnits = this.isMime ? this.attackerTeam.roster : [u];
      // Ctx for the slim legacy fallback (break skills / monster skills / Elixir).
      const legacyCtx = {
        pa: this.attacker.stats.pa, ma: this.attacker.stats.ma,
        brave: u.Brave, faith: u.Faith, wp: eq.Mainhand.wp, weaponType: eq.Mainhand.type,
        paUnarmed: this.attacker.paUnarmed,
      };
      const seen = new Set();
      for (const su of sourceUnits) {
        const names = [
          ...(su.ClassSkills || []),
          ...(su.ExtraSkills || []),
          ...((this.toolTips.MonsterSkills && this.toolTips.MonsterSkills[su.Class]) || []),
        ].map((n) => (n || '').replace('*', ''));
        for (const name of names) {
          if (seen.has(name)) continue;
          seen.add(name);
          const model = parseAbilityInfo(this.abilitiesInfo[name]);
          if (!model) {
            // Parser miss → legacy fallback so these stop vanishing from the
            // matrix. Jump is already its own row above; songs/breaks are
            // percents we don't show here, so only flat-damage forms are added.
            if (/^(Level|Vertical) Jump/.test(name)) continue;
            const legacy = legacyAbilityFormula(name, legacyCtx);
            if (legacy && legacy.kind === 'damage') {
              rows.push({ label: name, flat: legacy.value, element: null, evadable: true });
            }
            continue;
          }
          if (model.kind === 'number' && model.verb === 'Damage') {
            rows.push({ label: name, model, element: model.element, evadable: true });
          } else if (model.kind === 'percent') {
            rows.push({ label: name, model, element: model.element, status: true });
          }
        }
      }
      return rows;
    },

    // The grid: one row per ability/attack, one cell per defender.
    grid() {
      const ctxBase = {
        ma: this.attacker.stats.ma, pa: this.attacker.stats.pa, speed: this.attacker.stats.speed,
        brave: this.attackerUnit.Brave, faith: this.attackerUnit.Faith,
        wp: this.attacker.equipped.Mainhand.wp, maxHp: this.attacker.stats.hp,
        paUnarmed: this.attacker.paUnarmed,
      };
      return this.abilityRows.map((row) => {
        const isPercent = row.model && row.model.kind === 'percent';
        const aMods = row.attackerMods !== undefined ? row.attackerMods : this.attackerMods;
        return {
          ability: row.label,
          element: row.element,
          status: !!row.status,
          cells: this.defenders.map((d) => {
            // Legacy flat-damage ability: show the estimate + hit chance; no
            // modifier pipeline (empty steps -> "base damage" in the drill-down).
            if (row.flat != null) {
              const hc = (dir) => Math.round(hitChance(d, { type: 'physical', direction: dir }));
              const hitDirs = { front: hc('front'), side: hc('side'), back: hc('back'), magical: false };
              return { kind: 'damage', display: `${row.flat}`, steps: [], effect: null, hit: hitDirs.front, hitDirs };
            }
            // targetMa + targetEquipped feed Unyielding Cut's two-branch conditional:
            // the bare-target branch is PA*(WP+TargetMA), taken when the defender has no
            // non-shield gear (monsters carry none). Ignored by every other ability model.
            const ctx = {
              ...ctxBase, targetFaith: d.unit.Faith, targetMa: d.stats.ma,
              targetEquipped: !!(d.unit.Mainhand || d.unit.Head || d.unit.Armor || d.unit.Accessory
                || (d.unit.Offhand && d.equipped.Offhand.type !== 'Shield')),
            };
            if (isPercent) {
              const { final, steps } = computeSuccess(row.model, ctx, { ...aMods, ...d.mods });
              return { kind: 'percent', display: `${final}%`, steps, effect: null };
            }
            const { final, steps } = computeAbilityDamage(row.model, ctx, { ...aMods, ...d.mods });
            let effect = null;
            if (row.element) {
              if (d.profile.absorb.has(row.element)) effect = 'absorb';
              else if (d.mods.weak.has(row.element)) effect = 'weak';
              else if (d.profile.half.has(row.element)) effect = 'half';
            }
            const display = Array.isArray(final) ? `${final[0]} - ${final[1]}` : `${final}`;
            // Hit chance (front shown in the cell; all directions in the drill-down).
            let hit = null, hitDirs = null;
            if (row.evadable) {
              const type = row.model.magical ? 'magical' : 'physical';
              const hc = (dir) => Math.round(hitChance(d, { type, direction: dir }));
              hitDirs = { front: hc('front'), side: hc('side'), back: hc('back'), magical: type === 'magical' };
              hit = hitDirs.front;
            }
            return { kind: 'damage', display, steps, effect, hit, hitDirs };
          }),
        };
      });
    },

    openCell() {
      if (!this.expanded) return null;
      return this.grid[this.expanded.r] && this.grid[this.expanded.r].cells[this.expanded.c];
    },
  },

  methods: {
    // Battle sim: prep both rosters → combatant specs, run N battles → win%.
    simulate() {
      const data = { charStats: this.charStats, itemschanged: this.itemschanged, notAbles: this.notAbles, abilitiesInfo: this.abilitiesInfo, monsterSkills };
      const A = prepTeam(this.leftTeam.roster, data);
      const B = prepTeam(this.rightTeam.roster, data);
      const terrain = this.terrain, tiles = this.tiles; // real map geometry/height (null ⇒ flat grid)
      this.simResult = runBattles(A, B, { ...battleConfig, terrain, tiles });
      // The log is ONE representative battle, separate from the N-run average
      // above — capture its outcome so we can label it (else the log can show
      // the underdog winning beneath an "82%" bar). It runs on a recorded SEED so
      // the watched fight is reproducible: serializeRepro bundles seed+teams+map
      // into a capsule (📋 copy repro) you paste into replayRepro() to debug it.
      const seed = Math.floor(Math.random() * 0x7fffffff);
      const sample = simulateBattle(A, B, { ...battleConfig, frames: true, terrain, tiles, seed });
      this.simLog = sample.log || [];
      this.simFrames = sample.frames || [];
      this.simSample = { winner: sample.winner, turns: sample.turns };
      this.simRepro = serializeRepro(A, B, { ...battleConfig, terrain, tiles }, seed);
      this.reproCopied = false;
    },
    // Copy the sampled fight's reproducible capsule to the clipboard.
    copyRepro() {
      if (!this.simRepro || !navigator.clipboard) return;
      navigator.clipboard.writeText(this.simRepro).then(() => {
        this.reproCopied = true;
        setTimeout(() => { this.reproCopied = false; }, 1500);
      }).catch(() => {});
    },
    // Header for the sampled log battle (which side won that single run).
    sampleLabel() {
      const s = this.simSample;
      if (!s) return '';
      const who = s.winner === 'A' ? `${this.leftTeam.label} won`
                : s.winner === 'B' ? `${this.rightTeam.label} won` : 'draw';
      return `Sample battle — ${who} in ${s.turns} turns · one run, not the ${this.simRuns}-run average above`;
    },
    // 95% confidence margin on the win% (± points) — Wald interval from N runs.
    winMargin() {
      if (!this.simResult) return 0;
      const { aWinPct: p, n } = this.simResult;
      return Math.max(1, Math.round(196 * Math.sqrt((p * (1 - p)) / n)));
    },
    // Per-unit survival %, split by side, most-durable first ("who carries").
    survivalRows() {
      const us = (this.simResult && this.simResult.unitStats) || [];
      const sorted = [...us].sort((x, y) => y.survivalPct - x.survivalPct);
      return { A: sorted.filter((u) => u.team === 'A'), B: sorted.filter((u) => u.team === 'B') };
    },
    teamColor(label) { return teamBright(label); },
    // Display label for a lowercase team name (one of this matchup's two teams).
    teamLabelFor(name) {
      if (name === this.leftTeam.name) return this.leftTeam.label;
      if (name === this.rightTeam.name) return this.rightTeam.label;
      return name;
    },
    teamBoxStyle(label) {
      const c = this.teamColor(label);
      return { border: `2px solid ${c}`, background: `${c}22` };
    },
    portrait(unit, label) {
      if (unit.Gender === 'Monster') return PORTRAIT_BASE + unit.Class + '.gif';
      const g = (unit.Gender || 'Male')[0];
      if (label === 'Champion') return PORTRAIT_BASE + unit.Class + g + '.gif';
      return PORTRAIT_BASE + unit.Class + g + '_' + label + '.gif';
    },
    pick(side, i) {
      this.attackerSide = side;
      this.attackerIndex = i;
      this.expanded = null;
    },
    isOpen(r, c) { return this.expanded && this.expanded.r === r && this.expanded.c === c; },
    toggle(r, c) { this.expanded = this.isOpen(r, c) ? null : { r, c }; },
  },
};
</script>

<style scoped>
.fight-backdrop {
  padding: 24px 12px;
}
.fight-modal {
  position: relative;
  width: 100%;
  max-width: 1100px;
  background: rgba(12, 54, 80, 0.97);
  border: 2px solid #fff;
  color: #fff;
  padding: 20px 24px 24px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  font-size: 14px;
}
.close-btn {
  position: absolute; top: 6px; right: 10px;
  background: transparent; border: none; color: #fff;
  font-size: 28px; line-height: 1; cursor: pointer; padding: 4px 8px;
}
.close-btn:hover { color: #ff5555; }

.roster-select {
  margin-top: 6px;
  display: flex; flex-wrap: wrap; align-items: flex-start; justify-content: center;
  gap: 8px 18px; margin-bottom: 4px;
}
.roster-side {
  flex: 0 1 auto; position: relative; overflow: hidden;
  border-radius: 6px; padding: 6px 8px;
}
.roster-wm {
  position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
  font-family: "BossBattle"; font-size: 42px; line-height: 1; opacity: 0.22;
  text-transform: uppercase; white-space: nowrap; pointer-events: none;
  -webkit-text-stroke: 1px rgba(0, 0, 0, 0.4);
}
.roster-units { position: relative; z-index: 1; display: flex; flex-wrap: wrap; gap: 4px; justify-content: center; }
.roster-unit {
  display: flex; flex-direction: column; align-items: center; width: 62px;
  background: transparent; border: 1px solid transparent; border-radius: 5px;
  color: #fff; font-family: inherit; cursor: pointer; padding: 3px 2px;
}
.roster-unit:hover { background: rgba(255, 255, 255, 0.1); }
.roster-unit.active { border-color: #ffd700; background: rgba(255, 215, 0, 0.15); }
.ru-icon { width: 40px; height: 40px; object-fit: contain; }
/* Left team faces right, toward their opponents. */
.left-side .ru-icon { transform: scaleX(-1); }
.ru-name {
  font-size: 10px; line-height: 1.1; text-align: center; max-width: 100%;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.roster-vs { align-self: center; font-family: "BossBattle"; font-size: 16px; opacity: 0.8; }
.pick-hint { text-align: center; font-size: 11px; opacity: 0.6; margin: 0 0 10px; }
.fight-map { margin: 0 0 14px; }
.fight-map-label { font-size: 12px; opacity: 0.85; margin-bottom: 4px; color: #ffd479; }

/* Predict-the-Bracket pick bar inside the Fight View */
.fm-predict {
  display: flex; flex-wrap: wrap; align-items: center; justify-content: center;
  gap: 6px 10px; margin: 0 auto 12px; max-width: 560px; padding: 7px 12px;
  border-radius: 8px; font-size: 13px;
  background: rgba(124, 77, 255, 0.18); border: 1px solid rgba(159, 122, 234, 0.55);
}
.fm-predict.graded { background: rgba(0, 0, 0, 0.3); border-color: rgba(255, 255, 255, 0.18); }
.fmp-q { color: #d6c8ff; font-weight: bold; }
.fmp-btn {
  cursor: pointer; border: 2px solid var(--tc, #9fd0f0); border-radius: 6px;
  padding: 3px 14px; background: rgba(0, 0, 0, 0.35); color: #fff; font-weight: bold;
  font-size: 13px; transition: background 0.12s, box-shadow 0.12s;
}
.fmp-btn:hover { background: rgba(255, 255, 255, 0.1); }
.fmp-btn.picked { background: var(--tc, #7c4dff); box-shadow: 0 0 0 2px #fff, 0 0 10px var(--tc, rgba(214, 200, 255, 0.9)); }
.fmp-meta { color: #cbb8ff; opacity: 0.85; font-size: 12px; }
.fmp-res { font-weight: bold; }
.fmp-res.hit { color: #57e07a; }
.fmp-res.miss { color: #ff7b7b; }
.fmp-res.none { color: #b9c2cc; }
.fmp-sim { color: #cbe6ff; }

.turn-order {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  margin-bottom: 12px; padding: 6px 8px;
  border: 1px solid rgba(255, 255, 255, 0.18); background: rgba(0, 0, 0, 0.18);
}
.to-label { flex: 0 0 auto; font-size: 11px; opacity: 0.75; cursor: help; }
.to-strip { display: flex; gap: 4px; overflow-x: auto; }
.to-unit {
  flex: 0 0 auto; display: flex; flex-direction: column; align-items: center;
  width: 46px; border-radius: 4px; padding: 2px 1px; border-top: 3px solid transparent;
}
.to-left  { border-top-color: #4aa3ff; }
.to-right { border-top-color: #ff7b7b; }
.to-num { font-size: 10px; opacity: 0.6; }
.to-icon { width: 34px; height: 34px; object-fit: contain; }
.to-icon.flip { transform: scaleX(-1); }
.to-name {
  font-size: 9px; line-height: 1.05; text-align: center; max-width: 100%;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.to-status { font-size: 9px; }
.to-status.haste { color: #00e676; }
.to-status.slow { color: #ff7b7b; }

.fight-body { display: flex; flex-direction: column; gap: 14px; }
.attacker-panel {
  display: flex; flex-wrap: wrap; gap: 10px 18px; align-items: flex-start; justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(0, 0, 0, 0.18);
  padding: 10px 14px;
}
.ap-identity { display: flex; align-items: center; gap: 10px; }
.ap-portrait { width: 48px; height: 48px; object-fit: contain; }
.ap-name { font-weight: bold; font-size: 15px; }
.ap-class, .ap-bf { font-size: 12px; opacity: 0.85; }
.ap-block { font-size: 12px; line-height: 1.5; }
.ap-stats {
  display: grid;
  grid-template-columns: repeat(3, auto);
  gap: 2px 14px;
}
.ap-stats span { white-space: nowrap; }
.ap-affinity {
  border-left: 3px solid #ffd700; padding-left: 8px; align-self: stretch;
  display: flex; align-items: center;
}
.matrix-wrap { min-width: 0; overflow-x: auto; }

.fight-matrix { width: 100%; border-collapse: collapse; }
.fight-matrix th, .fight-matrix td {
  border: 1px solid rgba(255, 255, 255, 0.18); padding: 5px 8px; text-align: center;
}
.ability-col { text-align: left; white-space: nowrap; }
.el-tag {
  font-size: 10px; opacity: 0.7; border: 1px solid rgba(255,255,255,.3);
  border-radius: 3px; padding: 0 3px; margin-left: 4px;
}
.def-name { font-weight: bold; }
.def-class { font-size: 11px; opacity: 0.8; }
.def-zodiac { font-size: 11px; }
.z-good, .z-best { color: #00e676; }
.z-bad, .z-worst { color: #ff5555; }
.def-affinity { font-size: 10px; opacity: 0.75; }

.cell { cursor: pointer; }
.cell:hover { background: rgba(255, 255, 255, 0.1); }
.cell-dmg { display: block; }
.cell-hit { display: block; font-size: 10px; opacity: 0.6; }
.cell.open { outline: 2px solid #ffd700; }
.fx-weak { background: rgba(231, 76, 60, 0.28); }
.fx-half { background: rgba(52, 152, 219, 0.28); }
.fx-absorb { background: rgba(46, 204, 113, 0.28); }

.drilldown {
  margin-top: 10px; border: 1px solid #ffd700; padding: 8px 12px;
  background: rgba(255, 215, 0, 0.08);
}
.drill-head { font-weight: bold; margin-bottom: 6px; }
.drill-steps { display: flex; flex-wrap: wrap; gap: 8px; }
.drill-step { font-size: 12px; }
.step-label { opacity: 0.7; }
.step-factor { margin-left: 3px; opacity: 0.55; font-style: italic; }
.step-value { margin-left: 4px; font-weight: bold; }
.drill-hit { margin-top: 6px; font-size: 12px; opacity: 0.85; }

.status-row {
  display: flex; flex-wrap: wrap; gap: 12px; align-items: center;
  margin-top: 14px; padding-top: 10px; border-top: 1px solid rgba(255,255,255,.2);
  font-size: 12px;
}
.status-label { opacity: 0.8; }
.status-row label { display: flex; align-items: center; gap: 4px; cursor: pointer; }
.matrix-note { font-size: 11px; opacity: 0.65; margin: 8px 0 0; line-height: 1.4; }
.no-abilities { opacity: 0.7; font-style: italic; }
.mime-note {
  font-size: 12px; margin: 0 0 8px; padding: 6px 10px; line-height: 1.4;
  border-left: 3px solid #ffd700; background: rgba(255, 215, 0, 0.1);
}
.sim-bar { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; justify-content: center; margin: 6px 0; font-size: 14px; }
.sim-btn {
  background: rgba(12, 54, 80, 0.85); color: #fff; border: 1px solid #fff; border-radius: 4px;
  font-family: inherit; font-size: 13px; font-weight: bold; padding: 3px 12px; cursor: pointer;
}
.sim-btn:hover { background: #1C6EA4; }
.sim-pct { font-weight: bold; }
.sim-vs { opacity: 0.6; }
.sim-draw { opacity: 0.7; font-size: 12px; }
.sim-log-toggle { background: none; border: 1px solid #888; color: #cbe6ff; border-radius: 3px; font-size: 11px; padding: 1px 6px; cursor: pointer; }
.sim-note { opacity: 0.55; font-size: 11px; font-style: italic; }
.sim-map-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 4px; }
.sim-map { font-size: 12px; opacity: 0.85; color: #ffd479; }
.sim-map-flat { opacity: 0.55; color: #cdd; font-style: italic; }
.sim-repro-btn { flex: none; background: none; border: 1px solid #888; color: #cbe6ff; border-radius: 3px; font-size: 11px; padding: 1px 6px; cursor: pointer; white-space: nowrap; }
.sim-repro-btn:hover { border-color: #cbe6ff; }
.sim-log {
  max-height: 200px; overflow-y: auto; margin: 4px auto 8px; max-width: 600px;
  background: rgba(0, 0, 0, 0.4); border: 1px solid #2f5d86; border-radius: 4px;
  padding: 6px 10px; font-size: 11px; line-height: 1.5; text-align: left; font-family: monospace;
}
.sim-log-head { color: #ffd479; font-weight: bold; margin-bottom: 4px; position: sticky; top: 0; background: rgba(0, 0, 0, 0.85); padding: 2px 0; }
.sim-ci { font-size: 11px; opacity: 0.7; font-variant-numeric: tabular-nums; }
.sim-survival { display: flex; gap: 18px; justify-content: center; flex-wrap: wrap; margin: 4px auto 8px; max-width: 600px; font-size: 12px; }
.ss-col { min-width: 150px; }
.ss-head { font-weight: bold; font-size: 10px; text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 2px; opacity: 0.85; }
.ss-row { display: flex; justify-content: space-between; gap: 12px; opacity: 0.9; }
.ss-name { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 150px; }
.ss-pct { font-variant-numeric: tabular-nums; }
</style>
